import ProfileImg from '@atoms/ProfileImg';
import useCurrentEventInfo from '@contexts/useCurrentEventInfo';
import useMediaDeviceReadiness from '@hooks/useMediaDeviceReadiness';
import AvAccessButton from '@molecules/AvAccessButton';
import Camera from '@molecules/Camera';
import { useCreateVideoRoom, useGetRoom, useJoinRoom } from '@queries/rooms';
import { withAuthGate } from '@sunrise-ui/composites';
import {
  AuthFlowError,
  Button,
  ButtonVariants,
  MediaDeviceType,
  PageHeader,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  useMediaQueries,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import React from 'react';
import { StyleSheet, View } from 'react-native';

function GreenRoomScreen() {
  const { isMobile } = useMediaQueries();
  const styles = useDynamicStyles(createStyles);

  const { currentUserId } = useAuthStore();
  const { currentEventId, currentRoomId, setRoomToken } = useCurrentEventInfo();
  const { isReadyToRecord, errorMessage } = useMediaDeviceReadiness();
  const { data: room } = useGetRoom(currentRoomId);
  const { mutateAsync: createVideoRoom, isPending: isCreatingVideoRoom } =
    useCreateVideoRoom();
  const { mutateAsync: joinRoom, isPending: isJoiningRoom } = useJoinRoom();

  const handleReadyPress = async () => {
    try {
      if (!currentUserId) return;

      if (!room?.is_live) {
        await createVideoRoom({
          event_id: currentEventId,
          user_id: currentUserId,
          room_id: currentRoomId,
          experience_type: 'SUNRISE',
        });
      }

      const { token } = await joinRoom({
        joinRoomInput: {
          room_id: currentRoomId,
          user_id: currentUserId,
        },
      });
      setRoomToken(token);

      router.navigate(`/live/${currentEventId}`);
    } catch (error) {
      if (error instanceof AuthFlowError) return;
    }
  };

  return (
    <View style={styles.container}>
      <View style={[styles.roomHalf, styles.centered]}>
        {isReadyToRecord ? (
          <Camera />
        ) : (
          <ProfileImg
            userId={currentUserId}
            imgSize="large"
            styleSize={300}
            forcePhoto
          />
        )}
      </View>
      <View style={[styles.roomHalf, styles.card]}>
        <PageHeader
          header="Welcome to your room."
          subheader="Check your settings and let people in when you're ready."
        />
        <AvAccessButton variant={MediaDeviceType.CAMERA} />
        <AvAccessButton variant={MediaDeviceType.MICROPHONE} />
        <Button
          onPress={handleReadyPress}
          disabled={!isReadyToRecord || isCreatingVideoRoom || isJoiningRoom}
          loading={isCreatingVideoRoom || isJoiningRoom}
          size={isMobile ? 'medium' : 'large'}
          variant={ButtonVariants.PURPLE}
        >
          Ready
        </Button>
        {errorMessage && <TextReg>{errorMessage}</TextReg>}
      </View>
    </View>
  );
}

const createStyles = ({ isMobile, isTablet }: WithResponsive) =>
  StyleSheet.create({
    container: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      width: '100%',
      flex: 1,
      position: 'relative',
    },
    roomHalf: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      padding: isMobile ? 12 : isTablet ? 16 : 20,
    },
    centered: {
      justifyContent: isMobile ? 'space-around' : 'center',
    },
    card: {
      justifyContent: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? 12 : isTablet ? 16 : 20,
      padding: isMobile ? 12 : isTablet ? 16 : 20,
      width: '100%',
    },
  });

export default withAuthGate(GreenRoomScreen);
